import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

interface Workspace {
  id: string;
  name: string;
  role: string;
}

/**
 * WorkspacePicker page for OIDC authorization code flow.
 * Displayed when a user belongs to multiple workspaces.
 * Allows workspace selection before continuing the OAuth flow.
 */
export function WorkspacePickerPage() {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get the continue URL (encoded OAuth params)
  const continueUrl = searchParams.get('continue') || '';

  useEffect(() => {
    fetchWorkspaces();
  }, []);

  const fetchWorkspaces = async () => {
    try {
      const response = await fetch('/api/oidc/session/workspaces', {
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          // No session - redirect to login
          const loginUrl = continueUrl
            ? `/auth/login?continue=${encodeURIComponent(continueUrl)}`
            : '/auth/login';
          navigate(loginUrl);
          return;
        }
        throw new Error('Failed to load workspaces');
      }

      const data = await response.json();
      setWorkspaces(data.workspaces || []);

      // Auto-select if only one workspace
      if (data.workspaces?.length === 1) {
        await selectWorkspace(data.workspaces[0].id);
      }
    } catch (err) {
      setError('Failed to load workspaces. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const selectWorkspace = async (workspaceId: string) => {
    setSelectedId(workspaceId);
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/oidc/session/select-workspace', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ workspaceId }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to select workspace');
      }

      // If this selection is part of an OAuth/OIDC flow, continueUrl holds
      // the full path to resume (e.g. /api/oidc/authorize?response_type=code&...).
      // Honor a real continue target exactly. A bare `/` is NOT a real
      // continue target — it is the admin control plane (403s for non-admins) —
      // so treat `/` (and an empty value) as "no continue" and land the user
      // on the Central /home route instead.
      if (continueUrl && continueUrl !== '/') {
        window.location.href = continueUrl;
      } else {
        navigate('/home');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to select workspace');
      setSelectedId(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-700';
      case 'admin':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading workspaces...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">
            Select Workspace
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Choose which workspace to use
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="space-y-2">
          {workspaces.map((workspace) => (
            <button
              key={workspace.id}
              onClick={() => selectWorkspace(workspace.id)}
              disabled={isSubmitting}
              className={`w-full p-4 text-left border rounded-lg transition-colors ${
                selectedId === workspace.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">
                    {workspace.name}
                  </div>
                  <div className="text-sm text-gray-500 mt-0.5">
                    {workspace.id.slice(0, 8)}...
                  </div>
                </div>
                <span
                  className={`text-xs font-medium px-2 py-1 rounded ${getRoleBadgeColor(
                    workspace.role
                  )}`}
                >
                  {workspace.role}
                </span>
              </div>
            </button>
          ))}
        </div>

        {workspaces.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No workspaces available.</p>
            <p className="text-sm text-gray-400 mt-1">
              Please contact your administrator.
            </p>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={() => {
              const loginUrl = continueUrl
                ? `/auth/login?continue=${encodeURIComponent(continueUrl)}`
                : '/auth/login';
              navigate(loginUrl);
            }}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            ← Sign in with a different account
          </button>
        </div>
      </div>
    </div>
  );
}
